/**
 * Handles all state management for tournament registration process
 */

// Initial state structure
const initialState = {
  signUp: {},
  teamConfig: {},
  payment: {},
};

/**
 * Get saved progress from localStorage
 * @param {string} tournamentSlug - The tournament slug
 * @returns {Object} The saved progress data
 */
export const getSavedProgress = (tournamentSlug, userId) => {
  try {
    let savedProgress;
    if (userId) {
      savedProgress = localStorage.getItem(
        `tournament_${tournamentSlug}_progress${userId}`,
      );
    } else {
      savedProgress = localStorage.getItem(
        `tournament_${tournamentSlug}_progress`,
      );
    }
    if (savedProgress) {
      const parsed = JSON.parse(savedProgress);
      return parsed;
    }
  } catch (error) {
    console.error('Error reading from localStorage:', error);
  }
  return { step: 0, data: initialState };
};

/**
 * Save progress to localStorage
 * @param {string} tournamentSlug - The tournament slug
 * @param {number} step - The current step
 * @param {Object} newData - The new form data to save
 */
export const saveProgress = (tournamentSlug, step, newData, userId) => {
  try {
    // First get existing data
    const existingProgress = getSavedProgress(tournamentSlug, userId);
    const existingData = existingProgress.data || initialState;

    // Merge the new data with existing data
    const mergedData = {
      ...existingData,
      ...newData,
    };
    // Shallow merge would replace all of teamConfig when callers only pass { teams }.
    if (newData.teamConfig !== undefined) {
      mergedData.teamConfig = {
        ...(existingData.teamConfig || {}),
        ...newData.teamConfig,
      };
    }

    const dataToSave = {
      step,
      data: mergedData,
    };
    if (userId) {
      localStorage.setItem(
        `tournament_${tournamentSlug}_progress${userId}`,
        JSON.stringify(dataToSave),
      );
    } else {
      localStorage.setItem(
        `tournament_${tournamentSlug}_progress`,
        JSON.stringify(dataToSave),
      );
    }

    // Verify the save
    localStorage.getItem(`tournament_${tournamentSlug}_progress`);
  } catch (error) {
    console.error('Error saving progress:', error);
  }
};

/**
 * Update form data for a specific step
 * @param {Object} existingData - The existing form data
 * @param {number} step - The current step
 * @param {Object} stepData - The new data for the current step
 * @returns {Object} The updated form data
 */
export const updateFormData = (existingData, step, stepData) => {
  let newFormData = { ...existingData };

  switch (step) {
    case 0: // Sign Up
      newFormData = {
        ...existingData,
        signUp: { ...stepData },
      };
      break;
    case 1: // Team Configuration
      newFormData = {
        ...existingData,
        teamConfig: { ...stepData },
      };
      break;
    case 2: // Payment
      newFormData = {
        ...existingData,
        payment: { ...stepData },
      };
      break;
    default:
      break;
  }
  return newFormData;
};

/**
 * Clear saved progress
 * @param {string} tournamentSlug - The tournament slug
 * @param {string} userId - userID
 */
export const clearProgress = (tournamentSlug, userId) => {
  try {
    if (userId) {
      localStorage.removeItem(`tournament_${tournamentSlug}_progress${userId}`);
    } else {
      localStorage.removeItem(`tournament_${tournamentSlug}_progress`);
    }
    clearUserIdInLocalStorage();
  } catch (error) {
    console.error('Error clearing progress:', error);
  }
};

export const getUserIdFromLocalStorage = () => {
  return localStorage.getItem('current_user') || '';
};
export const setUserIdInLocalStorage = (user_id) => {
  localStorage.setItem('current_user', user_id);
};

export const clearUserIdInLocalStorage = () => {
  localStorage.removeItem('current_user');
};

/** Only use saved progress for authenticated users (resume last wizard step). */
export const getAuthenticatedProgressUserId = (user) => {
  const id = user?.id ?? user?.userId;
  return id != null && id !== '' ? String(id) : null;
};

/** Use during active signup before session is established. */
export const getSaveProgressUserId = (user) => {
  return getAuthenticatedProgressUserId(user) || getUserIdFromLocalStorage() || null;
};

/** Keep user on the tournament widget URL after login during an active registration session. */
const TOURNAMENT_WIDGET_KEY = 'tournament-widget';

export const setTournamentWidgetReturnPath = (tournamentSlug) => {
  if (tournamentSlug) {
    const path = `widgets/tournament/${tournamentSlug}`;
    localStorage.setItem(TOURNAMENT_WIDGET_KEY, path);
  }
};

export const getTournamentWidgetReturnPath = () =>
  localStorage.getItem(TOURNAMENT_WIDGET_KEY);

export const clearTournamentWidgetReturnPath = () => {
  localStorage.removeItem(TOURNAMENT_WIDGET_KEY);
};

const TOURNAMENT_REGISTRATION_STARTED_LOGGED_OUT_KEY =
  'tournament-registration-started-logged-out';
const TOURNAMENT_REGISTRATION_STARTED_LOGGED_IN_KEY =
  'tournament-registration-started-logged-in';
const TOURNAMENT_REGISTRATION_SIGN_IN_EXISTING_KEY =
  'tournament-registration-sign-in-existing';
const TOURNAMENT_REGISTRATION_ACTIVE_KEY = 'tournament-registration-active';
const TOURNAMENT_REGISTRATION_HIDE_NAV_KEY =
  'tournament-registration-hide-nav';

/** User is in the tournament registration wizard (persists through post-login redirect). */
export const markTournamentRegistrationActive = () => {
  localStorage.setItem(TOURNAMENT_REGISTRATION_ACTIVE_KEY, 'true');
};

export const isTournamentRegistrationActive = () =>
  localStorage.getItem(TOURNAMENT_REGISTRATION_ACTIVE_KEY) === 'true';

const clearTournamentRegistrationActive = () => {
  localStorage.removeItem(TOURNAMENT_REGISTRATION_ACTIVE_KEY);
};

/** User began registration while already logged in (normal in-app flow). */
export const markTournamentRegistrationStartedLoggedIn = () => {
  localStorage.setItem(TOURNAMENT_REGISTRATION_STARTED_LOGGED_IN_KEY, 'true');
};

const clearTournamentRegistrationStartedLoggedIn = () => {
  localStorage.removeItem(TOURNAMENT_REGISTRATION_STARTED_LOGGED_IN_KEY);
};

const wasTournamentRegistrationStartedLoggedIn = () =>
  localStorage.getItem(TOURNAMENT_REGISTRATION_STARTED_LOGGED_IN_KEY) ===
  'true';

/** User began registration while logged out (hide nav after new-account signup). */
export const markTournamentRegistrationStartedLoggedOut = () => {
  localStorage.setItem(TOURNAMENT_REGISTRATION_STARTED_LOGGED_OUT_KEY, 'true');
  markTournamentRegistrationActive();
  localStorage.removeItem(TOURNAMENT_REGISTRATION_SIGN_IN_EXISTING_KEY);
  clearTournamentRegistrationStartedLoggedIn();
};

export const clearTournamentRegistrationStartedLoggedOut = () => {
  localStorage.removeItem(TOURNAMENT_REGISTRATION_STARTED_LOGGED_OUT_KEY);
};

const wasTournamentRegistrationStartedLoggedOut = () =>
  localStorage.getItem(TOURNAMENT_REGISTRATION_STARTED_LOGGED_OUT_KEY) ===
  'true';

/** Existing account login via "Sign in here" — resume flow but keep nav visible. */
export const markTournamentRegistrationSignInExisting = () => {
  localStorage.setItem(TOURNAMENT_REGISTRATION_SIGN_IN_EXISTING_KEY, 'true');
  markTournamentRegistrationActive();
  clearTournamentRegistrationStartedLoggedOut();
  clearTournamentRegistrationStartedLoggedIn();
  localStorage.removeItem(TOURNAMENT_REGISTRATION_HIDE_NAV_KEY);
};

const clearTournamentRegistrationSignInExisting = () => {
  localStorage.removeItem(TOURNAMENT_REGISTRATION_SIGN_IN_EXISTING_KEY);
};

const isTournamentRegistrationSignInExisting = () =>
  localStorage.getItem(TOURNAMENT_REGISTRATION_SIGN_IN_EXISTING_KEY) ===
  'true';

export const setTournamentRegistrationHideNav = () => {
  localStorage.setItem(TOURNAMENT_REGISTRATION_HIDE_NAV_KEY, 'true');
};

const clearTournamentRegistrationHideNavOnly = () => {
  localStorage.removeItem(TOURNAMENT_REGISTRATION_HIDE_NAV_KEY);
};

export const beginTournamentRegistrationWhileLoggedIn = () => {
  clearTournamentRegistrationStartedLoggedOut();
  clearTournamentRegistrationHideNavOnly();
  markTournamentRegistrationStartedLoggedIn();
  markTournamentRegistrationActive();
};

/** Resume registration after new-account signup (user is logged in but nav stays hidden). */
export const continueNewAccountTournamentRegistration = () => {
  clearTournamentRegistrationStartedLoggedIn();
  markTournamentRegistrationActive();
};

export const clearTournamentRegistrationHideNav = () => {
  clearTournamentRegistrationHideNavOnly();
  clearTournamentRegistrationStartedLoggedOut();
  clearTournamentRegistrationSignInExisting();
  clearTournamentRegistrationStartedLoggedIn();
  clearTournamentRegistrationActive();
  clearTournamentWidgetReturnPath();
};

/** Logged-in coach left wizard — restore full app nav on /tournaments. */
export const exitTournamentRegistrationWizard = () => {
  clearTournamentRegistrationActive();
  clearTournamentRegistrationStartedLoggedOut();
  clearTournamentRegistrationSignInExisting();
  clearTournamentRegistrationStartedLoggedIn();
  clearTournamentWidgetReturnPath();
  clearTournamentRegistrationHideNavOnly();
};

/** New coach backed to tournaments list — keep focused view (no header/sidebar). */
export const exitTournamentRegistrationToListAsNewAccount = () => {
  clearTournamentRegistrationActive();
  clearTournamentRegistrationStartedLoggedOut();
  clearTournamentRegistrationSignInExisting();
  clearTournamentRegistrationStartedLoggedIn();
  clearTournamentWidgetReturnPath();
};

/** After a one-time post-login redirect into the tournament flow, stop future login redirects. */
export const completeTournamentLoginRedirect = () => {
  clearTournamentRegistrationSignInExisting();
  clearTournamentRegistrationStartedLoggedOut();
  clearTournamentWidgetReturnPath();
};

/** Clear all tournament registration session state (e.g. on logout). */
export const clearTournamentRegistrationSession = () => {
  clearTournamentRegistrationHideNav();
};

export const isTournamentRegistrationHideNavActive = () =>
  localStorage.getItem(TOURNAMENT_REGISTRATION_HIDE_NAV_KEY) === 'true'

/** New-account signup flow — stay on widget when backing out of tournament details. */
export const isNewAccountTournamentRegistrationFlow = () =>
  isTournamentRegistrationHideNavActive() &&
  !wasTournamentRegistrationStartedLoggedIn();

/** True while user should return to an in-progress tournament registration after login. */
export const shouldResumeTournamentRegistrationAfterLogin = () => {
  const tournamentWidget = getTournamentWidgetReturnPath();
  if (!tournamentWidget) {
    return false;
  }
  return (
    wasTournamentRegistrationStartedLoggedOut() ||
    isTournamentRegistrationSignInExisting() ||
    isTournamentRegistrationHideNavActive()
  );
};

/** Remove stale tournament-widget keys left from before session-scoped redirects. */
export const clearStaleTournamentRegistrationState = () => {
  if (!shouldResumeTournamentRegistrationAfterLogin()) {
    clearTournamentWidgetReturnPath();
  }
};

/** Hide nav only for new-account signup during registration, not existing-account sign-in. */
export const applyTournamentRegistrationHideNavIfStartedLoggedOut = () => {
  if (
    wasTournamentRegistrationStartedLoggedOut() &&
    !isTournamentRegistrationSignInExisting()
  ) {
    setTournamentRegistrationHideNav();
  }
};

const isTournamentWidgetPath = (path) =>
  /^\/widgets\/tournament\/[^/]+$/.test(path);

const isTournamentOrderConfirmationPath = (path) =>
  /^\/order\/confirmation\/[^/]+$/.test(path);

const isTournamentsListPath = (path) => path === '/tournaments';

export const shouldHideAppNavForTournamentRegistration = (path) =>
  isTournamentRegistrationHideNavActive() &&
  (isTournamentWidgetPath(path) ||
    isTournamentOrderConfirmationPath(path) ||
    isTournamentsListPath(path));
export default {
  initialState,
  getSavedProgress,
  saveProgress,
  updateFormData,
  clearProgress,
};
